import React from "react";
import { Divider, Select, Spin, Input, InputNumber } from "antd";
import { RefundType } from "../../Enums";

type Props = {
  orderDetails: any;
  refundTypeId: string | null;
  setRefundTypeId: (v: string) => void;
  refundTypes: any[];
  refundableAmount: number;
  loadingOrderDetails: boolean;
  returnItems: any[];
  returnTypes: any[];
  paymentMethods: any[];
  bankAccounts: any[];
  totalReturnValue: number;
  handleReturnQtyChange: (index: number, value: number) => void;
  handleReturnTypeChange: (index: number, value: string) => void;
  handleStockAdjustedChange: (index: number, value: number) => void;
  refundMethodId: string | null;
  setRefundMethodId: (v: string) => void;
  refundBankAccountId: string | null;
  setRefundBankAccountId: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
};

export const OrderReturnForm: React.FC<Props> = ({
  orderDetails,
  refundTypeId,
  setRefundTypeId,
  refundTypes,
  refundableAmount,
  loadingOrderDetails,
  returnItems,
  returnTypes,
  paymentMethods,
  bankAccounts,
  totalReturnValue,
  handleReturnQtyChange,
  handleReturnTypeChange,
  handleStockAdjustedChange,
  refundMethodId,
  setRefundMethodId,
  refundBankAccountId,
  setRefundBankAccountId,
  note,
  setNote,
}) => {
  return (
    <>
      <div className="flex flex-col gap-4">
        {/* INVOICE SUMMARY */}
        <div className="border border-gray-100 rounded-xl p-4 bg-white">
          <div className="flex justify-between items-center mb-2">
            <div>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                Invoice
              </div>
              <div className="text-base font-extrabold text-gray-800">
                {orderDetails?.order_no}
              </div>
            </div>

            <div className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded">
              {orderDetails?.order_date}
            </div>
          </div>

          <Divider className="my-3 border-gray-100" />

          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="text-left">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Customer
              </div>
              <div className="text-xs font-semibold text-gray-700 truncate">
                {orderDetails?.customer_name || "Walk-in"}
              </div>
            </div>

            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Total
              </div>
              <div className="text-xs font-bold text-gray-800">
                LKR {orderDetails?.grand_total?.toLocaleString() || "0.00"}
              </div>
            </div>

            <div>
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Paid
              </div>
              <div className="text-xs font-bold text-emerald-600">
                LKR {orderDetails?.paid_amount?.toLocaleString() || "0.00"}
              </div>
            </div>

            <div className="text-right">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Due
              </div>
              <div className="text-xs font-bold text-red-500">
                LKR {orderDetails?.due_amount?.toLocaleString() || "0.00"}
              </div>
            </div>
          </div>
        </div>

        {/* RETURN INFO */}
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <div className="mb-2 text-xs font-bold text-gray-500 uppercase tracking-wide">
            Return Information
          </div>

          <Select
            value={refundTypeId || undefined}
            onChange={setRefundTypeId}
            options={refundTypes?.map((b: any) => ({
              value: b.id,
              label: b.name,
            }))}
            placeholder={
              refundableAmount > 0 ? "Select Refund Type" : "No refund needed"
            }
            className="w-full h-9 text-xs"
          />
        </div>

        {/* ITEMS */}
        <div className="border border-gray-100 rounded-xl p-4 bg-white">
          <div className="mb-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
            Return Items
          </div>

          {loadingOrderDetails ? (
            <div className="text-center py-8">
              <Spin size="small" />
            </div>
          ) : returnItems.length === 0 ? (
            <div className="text-center text-xs text-gray-400 py-4 italic">
              No returnable items.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {returnItems.map((item, index) => (
                <div
                  key={item.order_item_id}
                  className="flex items-center justify-between gap-3 py-3 border-b border-gray-50 last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-800 truncate">
                      {item.product_name}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      Batch: {item.batch_no || "-"} | Unit: {item.unit || "-"} |
                      Price: LKR {item.price}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <InputNumber
                      min={0}
                      max={item.max_quantity}
                      value={item.quantity}
                      onChange={(v) => handleReturnQtyChange(index, v || 0)}
                      size="small"
                      className="w-[70px]"
                    />

                    <Select
                      size="small"
                      className="w-[120px]"
                      value={item.return_type_id}
                      onChange={(v) => handleReturnTypeChange(index, v)}
                      options={returnTypes.map((t: any) => ({
                        value: t.id,
                        label: t.name,
                      }))}
                    />

                    <Select
                      size="small"
                      className="w-[100px]"
                      value={item.stock_adjusted}
                      onChange={(v) => handleStockAdjustedChange(index, v || 0)}
                      options={[
                        { value: 1, label: "Restore" },
                        { value: 0, label: "No stock" },
                      ]}
                    />

                    <div className="w-[90px] text-right text-xs font-bold text-gray-700">
                      LKR {item.total_refund?.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Divider className="my-4 border-gray-100" />

          {/* SUMMARY */}
          <div className="flex gap-4">
            <div className="flex-1 bg-gray-50/50 p-3 rounded-lg border border-gray-100">
              <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                Return Value
              </div>
              <strong className="text-sm font-extrabold text-gray-800">
                LKR {totalReturnValue.toLocaleString()}
              </strong>
            </div>

            <div className="flex-1 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
              <div className="text-[10px] text-blue-500 font-bold uppercase tracking-wider">
                Refundable
              </div>
              <strong className="text-sm font-extrabold text-blue-800">
                LKR {refundableAmount.toLocaleString()}
              </strong>
            </div>
          </div>

          {/* PAYMENT */}
          {refundableAmount > 0 &&
            refundTypeId === RefundType.CASH_REF && (
              <>
                <Divider className="my-4 border-gray-100" />
                <div className="flex gap-3">
                  <Select
                    value={refundMethodId || undefined}
                    onChange={setRefundMethodId}
                    placeholder="Payment method"
                    options={paymentMethods.map((m: any) => ({
                      value: m.id,
                      label: m.name,
                    }))}
                    className="w-1/2 h-9 text-xs"
                  />

                  <Select
                    value={refundBankAccountId || undefined}
                    onChange={setRefundBankAccountId}
                    placeholder="Bank account"
                    options={bankAccounts.map((b: any) => ({
                      value: b.id,
                      label: b.bank_name,
                    }))}
                    className="w-1/2 h-9 text-xs"
                  />
                </div>
              </>
            )}

          <div className="mt-4">
            <Input.TextArea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional return notes..."
              className="rounded-lg text-xs"
            />
          </div>
        </div>
      </div>
    </>
  );
};
